import { useQuery } from '@tanstack/react-query';
import { getStatusImagePromise, GetStatusImagePromiseParams } from 'src/utils/api';
import { QUERY_KEY_MAP } from 'src/constants';
import { useSearch } from 'src/hooks/use-search';
import { useIsZoneSandboxContext } from 'src/hooks/use-is-zone-sandbox-context';

//const STALE_TIME = 60 * 3 * 1000; // 3 minutes as signed url can expire

const fetchStatusImageBlobForCanvas = async (url: string): Promise<Blob | null> => {
  if (!url?.trim()?.length) {
    return null;
  }

  try {
    const res = await fetch(url);
    if (!res.ok) {
      throw new Error('Failed to fetch status image');
    }
    return res.blob();
  } catch (error) {
    console.error(error);
    throw new Error('Failed to fetch status image');
  }
};

export const useGetStatusImageUrl = ({
  display_id,
  surrounding_count,
  timestamp,
  fetchImageData,
  refetchInterval,
  customEnabled,
}: Partial<
  GetStatusImagePromiseParams & {
    fetchImageData?: boolean;
    refetchInterval?: number;
    customEnabled?: boolean;
  }
>) => {
  const [searchParams] = useSearch();
  const deviceId = display_id || searchParams?.device?.id || '';
  const isZoneSandboxContext = useIsZoneSandboxContext();

  const { data, isLoading, error, isRefetching } = useQuery({
    queryKey: [QUERY_KEY_MAP.useGetStatusImage, deviceId, surrounding_count, timestamp],
    queryFn: () => getStatusImagePromise({ display_id: deviceId, surrounding_count, timestamp }),
    enabled: typeof customEnabled === 'boolean' ? customEnabled : !isZoneSandboxContext && !!deviceId,
    staleTime: 0,
    gcTime: 0,
    refetchInterval,
  });

  const {
    data: imageData,
    isLoading: imageDataLoading,
    error: imageDataError,
    isRefetching: imageDataRefetching,
  } = useQuery({
    queryKey: [`${QUERY_KEY_MAP.useGetStatusImage}-image-data`, data, fetchImageData],
    queryFn: () => fetchStatusImageBlobForCanvas(data?.[0]?.url || ''),
    enabled: !isZoneSandboxContext && !!deviceId && fetchImageData && !!data?.[0]?.url,
  });

  return {
    data,
    isLoading,
    error,
    isRefetching,
    imageDataLoading,
    imageData,
    imageDataError,
    imageDataRefetching,
  };
};
